import { useState } from 'react';
import BudgetCreation from './BudgetCreation';
import AcceptInviteModal from '../Invites/AcceptInviteModal';
import LogoutButton from '../Auth/LogoutButton';
import styles from './BudgetChoice.module.css';

/**
 * BudgetChoice Component
 *
 * Initial screen for users with no budgets
 * Offers two options:
 * 1. Accept an invite code to join an existing budget
 * 2. Create a new budget
 *
 * Props:
 * - userEmail (string): Current user's email
 * - onBudgetCreated: Callback when budget is created
 * - onInviteAccepted: Callback when invite is accepted (receives budget info)
 * - getAccessTokenSilently: Auth0 function to get access token
 */
function BudgetChoice({ userEmail, onBudgetCreated, onInviteAccepted, getAccessTokenSilently }) {
    const [selectedOption, setSelectedOption] = useState(null);
    const [showAcceptModal, setShowAcceptModal] = useState(false);

    // User selected "Create New Budget"
    if (selectedOption === 'create') {
        return (
            <BudgetCreation
                onBudgetCreated={onBudgetCreated}
                onCancel={() => setSelectedOption(null)}
            />
        );
    }

    // User selected "Accept Invite"
    const handleAcceptSuccess = (budgetInfo) => {
        setShowAcceptModal(false);
        if (onInviteAccepted) {
            onInviteAccepted(budgetInfo);
        }
    };

    // Show choice screen
    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.header}>
                    <h2 className={styles.title}>
                        Welcome to Budget Tracker
                    </h2>
                    <LogoutButton />
                </div>
                <p className={styles.description}>
                    Get started by choosing one of the options below:
                </p>

                {/* Accept Invite Option */}
                <div
                    className={styles.acceptInviteOption}
                    onClick={() => setShowAcceptModal(true)}
                >
                    <h3 className={styles.optionTitlePrimary}>
                        Accept an Invite
                    </h3>
                    <p className={styles.optionDescription}>
                        Have an invite code? Join an existing budget that someone has shared with you.
                    </p>
                </div>

                {/* Create Budget Option */}
                <div
                    className={styles.createBudgetOption}
                    onClick={() => setSelectedOption('create')}
                >
                    <h3 className={styles.optionTitleSuccess}>
                        Create a New Budget
                    </h3>
                    <p className={styles.optionDescription}>
                        Start fresh by creating your own budget. You'll be the owner and can invite others later.
                    </p>
                </div>
            </div>

            {/* Accept Invite Modal */}
            {showAcceptModal && (
                <AcceptInviteModal
                    userEmail={userEmail}
                    onClose={() => setShowAcceptModal(false)}
                    onSuccess={handleAcceptSuccess}
                    getAccessTokenSilently={getAccessTokenSilently}
                />
            )}
        </div>
    );
}

export default BudgetChoice;
